import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Chevrones } from './iconos';

/**
 * Campo de selección que **se busca escribiendo en el propio campo**.
 *
 * ── Por qué se rehizo ──
 * La primera versión era un botón que abría un panel, y dentro del panel había
 * OTRA casilla de búsqueda. Dos controles para una sola intención: uno abre, el
 * otro filtra, y el que se ve —el campo— no acepta que le escriban. Jonathan lo
 * señaló mirando el selector de país.
 *
 * Acá el campo **es** la casilla de búsqueda. Se enfoca, se escribe, la lista de
 * abajo se filtra. Cerrado muestra lo elegido; enfocado muestra lo que se teclea.
 *
 * ── Lo que hace falta para que un combo sea usable de verdad ──
 * · **Teclado completo**: flechas para recorrer, Enter para elegir, Escape para
 *   cancelar sin perder lo que ya estaba. Sin esto es un desplegable bonito que
 *   obliga a soltar el teclado en medio de un formulario.
 * · **La selección se restaura al salir sin elegir.** Si alguien enfoca, escribe
 *   media palabra y se va con Tab, lo que había no se pierde.
 * · **`aria-activedescendant`** en vez de mover el foco a la opción: el foco tiene
 *   que quedarse en el campo para poder seguir escribiendo, pero el lector de
 *   pantalla necesita saber cuál está resaltada.
 */
export type OpcionCombo = {
  clave: number | string;
  texto: string;
  /** Bandera, logo, lo que ancle visualmente la fila. */
  icono?: ReactNode;
  /** Texto extra por el que también se puede buscar. Por ejemplo, el prefijo `+58`. */
  alterno?: string;
  /** Se pinta al final de la fila, en gris. */
  detalle?: string;
};

export function Combo({
  etiqueta,
  opciones,
  valor,
  alCambiar,
  alCrear,
  crearRotulo = 'Agregar',
  vacio = 'Sin especificar',
  error,
  sinCoincidencias = 'Sin coincidencias.',
}: {
  etiqueta: string;
  opciones: OpcionCombo[];
  valor: number | string | null;
  alCambiar: (clave: number | string | null) => void;
  /**
   * Da de alta lo escrito y devuelve la opción creada. Sin esto, el combo no
   * ofrece crear nada — que es lo correcto para quien no tiene el permiso.
   */
  alCrear?: (texto: string) => Promise<OpcionCombo>;
  crearRotulo?: string;
  vacio?: string;
  error?: string | undefined;
  sinCoincidencias?: string;
}) {
  const id = useId();
  const contenedor = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const lista = useRef<HTMLUListElement>(null);

  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState('');
  const [resaltada, setResaltada] = useState(0);
  const [creando, setCreando] = useState(false);

  const elegida = opciones.find((o) => o.clave === valor) ?? null;

  const visibles = useMemo(() => {
    const t = consulta.trim().toLowerCase();
    if (!t) return opciones;
    return opciones.filter(
      (o) => o.texto.toLowerCase().includes(t) || (o.alterno ?? '').toLowerCase().includes(t),
    );
  }, [opciones, consulta]);

  // Se compara normalizado igual que el índice único de la base: sin espacios en
  // los bordes y sin distinguir mayúsculas. Si acá se comparara distinto, se
  // ofrecería crear algo que el servidor va a rechazar por duplicado.
  const yaExiste = useMemo(
    () => opciones.some((o) => o.texto.trim().toLowerCase() === consulta.trim().toLowerCase()),
    [opciones, consulta],
  );
  const puedeCrear = !!alCrear && consulta.trim().length >= 2 && !yaExiste;

  /** Filas navegables: las visibles, y al final la de crear si aplica. */
  const total = visibles.length + (puedeCrear ? 1 : 0);

  function abrir() {
    if (abierto) return;
    setAbierto(true);
    setConsulta('');
    setResaltada(0);
  }

  /** Cierra sin elegir. Lo que estaba seleccionado NO se toca. */
  function cancelar() {
    setAbierto(false);
    setConsulta('');
  }

  function elegir(o: OpcionCombo) {
    alCambiar(o.clave);
    setAbierto(false);
    setConsulta('');
  }

  async function crear() {
    if (!alCrear || creando) return;
    setCreando(true);
    try {
      const nueva = await alCrear(consulta.trim());
      alCambiar(nueva.clave);
      setAbierto(false);
      setConsulta('');
    } finally {
      setCreando(false);
    }
  }

  useEffect(() => {
    if (!abierto) return;
    const alApuntar = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) cancelar();
    };
    /*
      `mousedown` y no `click`: con `click`, el cierre por clic afuera se disparaba
      ANTES que el clic de la opción y no se seleccionaba nada. Es el mismo motivo
      por el que las opciones usan `onMouseDown` con `preventDefault`.
    */
    document.addEventListener('mousedown', alApuntar);
    return () => document.removeEventListener('mousedown', alApuntar);
  }, [abierto]);

  // La opción resaltada tiene que quedar a la vista al moverse con las flechas.
  useEffect(() => {
    if (!abierto) return;
    lista.current
      ?.querySelector(`[data-indice="${resaltada}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [resaltada, abierto]);

  function alTeclear(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!abierto) return abrir();
      if (total === 0) return;
      setResaltada((i) => (e.key === 'ArrowDown' ? (i + 1) % total : (i - 1 + total) % total));
      return;
    }
    if (e.key === 'Enter') {
      if (!abierto) return;
      e.preventDefault();
      const opcion = visibles[resaltada];
      if (opcion) elegir(opcion);
      else if (puedeCrear) void crear();
      return;
    }
    if (e.key === 'Escape') {
      if (!abierto) return;
      // Se detiene acá: si no, el `<dialog>` de arriba lo toma como «cerrar el
      // modal» y se pierde el formulario entero por querer cerrar una lista.
      e.preventDefault();
      e.stopPropagation();
      cancelar();
      return;
    }
    if (e.key === 'Tab') cancelar();
  }

  const idLista = `${id}-lista`;

  return (
    <div className="relative" ref={contenedor}>
      <label
        htmlFor={id}
        className="pointer-events-none absolute -top-2 left-3 z-10 rounded bg-white px-1.5 text-xs font-semibold text-marino-500"
      >
        {etiqueta}
      </label>

      <div
        className={`flex w-full items-center gap-2 rounded-2xl border bg-white px-4 py-3.5
          transition
          ${
            error
              ? 'border-red-400 focus-within:ring-4 focus-within:ring-red-500/10'
              : 'border-marino-200 hover:border-marino-400 focus-within:border-marino-700 focus-within:ring-4 focus-within:ring-marino-700/10'
          }`}
      >
        {/* El ícono de lo elegido se mantiene mientras no se esté escribiendo. */}
        {!abierto && elegida?.icono}

        <input
          id={id}
          ref={entrada}
          role="combobox"
          aria-expanded={abierto}
          aria-controls={idLista}
          aria-autocomplete="list"
          aria-activedescendant={abierto && visibles[resaltada] ? `${id}-op-${resaltada}` : undefined}
          aria-invalid={error ? true : undefined}
          autoComplete="off"
          /*
            Cerrado muestra lo elegido; abierto, lo que se teclea. Es lo que hace
            que el campo sea a la vez la etiqueta y el buscador.
          */
          value={abierto ? consulta : (elegida?.texto ?? '')}
          placeholder={abierto ? 'Escribe para buscar…' : vacio}
          onFocus={abrir}
          onClick={abrir}
          onChange={(e) => {
            setConsulta(e.target.value);
            setResaltada(0);
            if (!abierto) setAbierto(true);
          }}
          onKeyDown={alTeclear}
          onBlur={cancelar}
          className="min-w-0 flex-1 bg-transparent text-marino-900 outline-none placeholder:text-marino-400/70"
        />

        {elegida && !abierto && (
          <button
            type="button"
            aria-label={`Quitar ${elegida.texto}`}
            // `onMouseDown` con `preventDefault`: con `onClick`, el campo perdía el
            // foco primero, `onBlur` cerraba, y el clic caía en el vacío.
            onMouseDown={(e) => {
              e.preventDefault();
              alCambiar(null);
            }}
            className="shrink-0 rounded-full px-1.5 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
          >
            ✕
          </button>
        )}
        <Chevrones size={14} className="shrink-0 text-marino-400" />
      </div>

      {error && <p className="mt-1.5 pl-5 text-xs font-medium text-red-600">{error}</p>}

      {abierto && (
        <ul
          id={idLista}
          ref={lista}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-2xl border
            border-marino-100 bg-white p-1 shadow-[var(--sombra-md)]"
        >
          {visibles.map((o, i) => (
            <li key={o.clave} data-indice={i}>
              <button
                type="button"
                id={`${id}-op-${i}`}
                role="option"
                aria-selected={o.clave === valor}
                onMouseEnter={() => setResaltada(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // No dejar que el campo pierda el foco antes de elegir.
                  elegir(o);
                }}
                className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm
                  transition ${i === resaltada ? 'bg-marino-50 text-marino-900' : 'text-marino-800'}`}
              >
                {o.icono}
                <span className="min-w-0 flex-1 truncate">{o.texto}</span>
                {o.detalle && (
                  <span className="shrink-0 tabular-nums text-marino-400">{o.detalle}</span>
                )}
              </button>
            </li>
          ))}

          {puedeCrear && (
            <li data-indice={visibles.length}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                disabled={creando}
                onMouseEnter={() => setResaltada(visibles.length)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  void crear();
                }}
                className={`flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm
                  font-semibold text-naranja-700 transition disabled:opacity-60
                  ${resaltada === visibles.length ? 'bg-naranja-50' : ''}`}
              >
                {creando ? 'Agregando…' : `${crearRotulo} «${consulta.trim()}»`}
              </button>
            </li>
          )}

          {total === 0 && (
            <li className="px-3 py-2 text-sm text-marino-400">
              {alCrear && consulta.trim().length > 0
                ? 'Escribe al menos dos letras para agregarlo.'
                : sinCoincidencias}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
